import * as Y from 'yjs';
import {WebsocketProvider} from 'y-websocket';
import {IndexeddbPersistence} from 'y-indexeddb';
import {HosannaWorkspace} from './HosannaWorkspace';
import {getDeviceInfo} from '../storage/localStorage';
import {ADORANS_WORKSPACE_ID} from '../../shared/services/workspaceAccess';
import {getWorkerSocket} from './create-yjs-worker';
import {adoptHosannaGeneration, hosannaDatabaseName, purgeStaleHosannaDatabases, readHosannaGeneration} from './hosannaGeneration';
import {applyHosannaPresenceRole} from './hosanna-presence-role';

// Every reconnect obtains a fresh single-use ticket. The public legacy session
// ID is the explicitly authorized capability; no Hosanna server secret in UI.
// A ticket for another room generation means the server reset the document (ADR 154): never
// connect the old local copy; `onGenerationChange` switches to a fresh store instead.
export function ticketWebSocket(token, endpoint, NativeSocket = window.WebSocket,
  {localGeneration = 0, onGenerationChange = () => {}} = {}) {
  return class TicketSocket {
    constructor(url) {
      this.OPEN = 1; this.readyState = 0; this.closed = false;
      this.controller = new AbortController();
      this.open(url);
    }
    get binaryType() {return this.socket?.binaryType || this._binaryType;}
    set binaryType(value) {this._binaryType = value; if (this.socket) this.socket.binaryType = value;}
    async open(url) {
      const timeout = setTimeout(() => this.controller.abort(), 10000);
      try {
        const response = await fetch(endpoint, {method: 'POST', headers: {Authorization: `Bearer ${token}`, 'Content-Type': 'application/json'},
          body: JSON.stringify({roomId: 'adorans', userName: getDeviceInfo().name || 'Chwała'}), signal: this.controller.signal});
        if (!response.ok) throw new Error(`Hosanna HTTP ${response.status}`);
        const {ticket, generation = 0} = await response.json();
        if (!ticket) throw new Error('Hosanna returned no WebSocket ticket');
        if (this.closed) return;
        if (generation !== localGeneration) {onGenerationChange(generation); throw new Error('Hosanna room generation changed');}
        const target = new URL(url); target.searchParams.set('ticket', ticket); target.searchParams.set('gen', String(generation));
        this.socket = new NativeSocket(target.href); this.socket.binaryType = this._binaryType || 'arraybuffer';
        this.socket.onopen = event => {this.readyState = 1; this.onopen?.(event);};
        this.socket.onmessage = event => this.onmessage?.(event);
        this.socket.onerror = event => this.onerror?.(event);
        this.socket.onclose = event => {this.readyState = 3; this.closed = true; this.onclose?.(event);};
      } catch (error) {
        if (!this.closed) {this.readyState = 3; this.closed = true; this.onerror?.(error); this.onclose?.({code: 1006});}
      } finally {clearTimeout(timeout);}
    }
    send(data) {this.socket.send(data);}
    close() {
      if (this.closed) return;
      this.closed = true; this.controller.abort(); this.readyState = 3;
      if (this.socket) this.socket.close(); else this.onclose?.({code: 1000});
    }
  };
}

export function connectHosanna(service, wsId, filePath) {
  if (!wsId?.startsWith('songs-')) throw new Error('Workspace ID is required and must start with songs-');
  if (!service.hosannaWorkspaces) service.hosannaWorkspaces = new Map();
  if (!service.docs.has(wsId)) {
    const source = new Y.Doc();
    const allowed = wsId === ADORANS_WORKSPACE_ID;
    const localGeneration = readHosannaGeneration();
    const database = hosannaDatabaseName(localGeneration);
    purgeStaleHosannaDatabases([database]);
    const base = process.env.REACT_APP_HOSANNA_YJS_URL || `${window.location.protocol === 'https:' ? 'wss:' : 'ws:'}//${window.location.host}/hosanna`;
    const ticketUrl = new URL(base.replace(/^ws/, 'http')); ticketUrl.pathname += '/ws-ticket';
    const provider = new WebsocketProvider(base, 'adorans', source, {connect: allowed, disableBc: true,
      WebSocketPolyfill: ticketWebSocket(wsId.slice('songs-storage-'.length), ticketUrl.href, getWorkerSocket(), {
        localGeneration, onGenerationChange: generation => {
          provider.shouldConnect = false;
          if (!adoptHosannaGeneration(generation)) console.error('Hosanna document was reset; reload Chwala to continue');
        }})});
    // Separate native cache: never merge the legacy path-based document.
    // Hydrate first to avoid creating duplicate nodes on an empty cold start.
    // Once hydrated, local writes must survive a lost network connection.
    let hydrated = provider.synced;
    if (allowed) {
      source.persistence = new IndexeddbPersistence(database, source);
      source.persistence.on('synced', () => {hydrated = true;});
    }
    provider.on('sync', synced => {if (synced) hydrated = true;});
    source.awareness = provider.awareness;
    const workspace = new HosannaWorkspace(source, () => allowed && hydrated);
    provider.awareness.setLocalStateField('user', {id: getDeviceInfo().guid, name: getDeviceInfo().name || 'Chwała'});
    // Hosanna's presence identity includes the device name; it uses clientId rather than GUID.
    provider.awareness.setLocalStateField('name', getDeviceInfo().name || 'Chwała');
    applyHosannaPresenceRole(provider);
    service.docs.set(wsId, workspace.doc); service.providers.set(wsId, provider); service.hosannaWorkspaces.set(wsId, workspace);
  }
  const doc = service.docs.get(wsId), provider = service.providers.get(wsId);
  if (!filePath) return {doc, provider};
  return {doc, provider, text: doc.getText(`content:${filePath}`), metadata: doc.getMap(`metadata:${filePath}`),
    filePath, contentPath: `content:${filePath}`, metadataPath: `metadata:${filePath}`};
}
